<template>
  <v-dialog
    :value="value"
    @input="$emit('input', $event)"
    :max-width="fullscreen ? '100vw' : '1100px'"
    :fullscreen="fullscreen"
    :overlay-opacity="0.55"
    :content-class="'fpm-dialog-root' + (fullscreen ? ' fpm-dialog-root--fs' : '')"
    @keydown.esc="close"
  >
    <div v-if="fileItem" class="fpm" :class="{ 'fpm--fs': fullscreen }">
      <header class="fpm-header">
        <div class="fpm-header-left">
          <img class="fpm-thumb" :src="fileIconSrc" :alt="kindLabel" />
          <div class="fpm-title">
            <h2 class="fpm-filename" :title="fileItem.name">{{ fileItem.name }}</h2>
            <div class="fpm-meta">
              <span class="fpm-chip">{{ kindLabel }}</span>
              <span v-if="sizeLabel" class="fpm-dot">·</span>
              <span v-if="sizeLabel">{{ sizeLabel }}</span>
              <span v-if="dateLabel" class="fpm-dot">·</span>
              <span v-if="dateLabel">{{ dateLabel }}</span>
            </div>
          </div>
        </div>

        <div class="fpm-actions">
          <button
            v-if="canPopOut"
            type="button"
            class="fpm-icon-btn"
            :title="$t('page_2.file_preview.open_in_window')"
            @click="popOut"
          >
            <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 2h5v5"/><path d="M14 2L7 9"/><path d="M11 9v4a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h4"/>
            </svg>
          </button>
          <button
            type="button"
            class="fpm-icon-btn"
            :title="fullscreen ? $t('page_2.file_preview.exit_fullscreen') : $t('page_2.file_preview.fullscreen')"
            @click="fullscreen = !fullscreen"
          >
            <svg v-if="!fullscreen" width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
              <path d="M2 6V2h4"/><path d="M14 6V2h-4"/><path d="M2 10v4h4"/><path d="M14 10v4h-4"/>
            </svg>
            <svg v-else width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
              <path d="M6 2v4H2"/><path d="M10 2v4h4"/><path d="M6 14v-4H2"/><path d="M10 14v-4h4"/>
            </svg>
          </button>
          <a
            v-if="downloadUrl"
            class="fpm-icon-btn"
            :href="downloadUrl"
            :download="fileItem.name"
            :title="$t('page_2.file_preview.download')"
          >
            <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
              <path d="M8 2v9"/><path d="M4 7l4 4 4-4"/><path d="M2 14h12"/>
            </svg>
          </a>
          <button
            type="button"
            class="fpm-icon-btn fpm-icon-btn--close"
            :title="$t('page_2.file_preview.close')"
            @click="close"
          >
            <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round">
              <path d="M3 3l10 10M13 3L3 13"/>
            </svg>
          </button>
        </div>
      </header>

      <FilePreviewBody
        :fileItem="fileItem"
        :groupId="groupId"
        :active="value"
      />


      <footer class="fpm-footer">
        <a class="fpm-link" :href="fileItem.webUrl" target="_blank" rel="noopener">
          {{ $t('page_2.file_preview.open_in_sharepoint') }}
        </a>
        <a
          v-if="officeProto"
          class="fpm-btn fpm-btn--primary"
          :href="officeProto"
        >
          {{ $t('page_2.file_preview.edit_in_app') }}
        </a>
      </footer>
    </div>
  </v-dialog>
</template>

<script>
import moment from "moment";
import {
  classifyFile,
  formatFileSize,
  officeProtocolUrl,
  getFileExtension,
  shouldOpenInPreview,
} from "@/helpers/file";
import FilePreviewBody from "./FilePreviewBody.vue";

const ICON_BY_EXT = {
  pdf: require("@/assets/img/pdf_icon.svg"),
  doc: require("@/assets/img/doc_icon.svg"),
  docx: require("@/assets/img/doc_icon.svg"),
  dotx: require("@/assets/img/doc_icon.svg"),
  dotm: require("@/assets/img/doc_icon.svg"),
  xls: require("@/assets/img/xls_icon.svg"),
  xlsx: require("@/assets/img/xls_icon.svg"),
  csv: require("@/assets/img/xls_icon.svg"),
  ppt: require("@/assets/img/ppt-icon.png"),
  pptx: require("@/assets/img/ppt-icon.png"),
  jpg: require("@/assets/img/jpg_icon.svg"),
  jpeg: require("@/assets/img/jpg_icon.svg"),
  png: require("@/assets/img/png_icon.svg"),
};
const DEFAULT_ICON = require("@/assets/img/dat_icon.svg");

export default {
  name: "FilePreviewModal",
  components: { FilePreviewBody },
  props: {
    value: { type: Boolean, default: false },
    fileItem: { type: Object, default: null },
    groupId: { type: String, default: "" },
  },
  data() {
    return {
      fullscreen: false,
    };
  },
  computed: {
    kind() {
      return classifyFile(this.fileItem);
    },
    downloadUrl() {
      return this.fileItem && this.fileItem["@microsoft.graph.downloadUrl"];
    },
    officeProto() {
      return this.fileItem ? officeProtocolUrl(this.fileItem) : null;
    },
    sizeLabel() {
      return this.fileItem ? formatFileSize(this.fileItem.size) : "";
    },
    dateLabel() {
      const d = this.fileItem && this.fileItem.lastModifiedDateTime;
      if (!d) return "";
      const m = moment(d);
      return m.isValid() ? m.format("D. MMM YYYY.") : "";
    },
    kindLabel() {
      const map = {
        pdf: "PDF",
        image: "IMG",
        office: "DOC",
        text: "TXT",
        video: "VID",
        audio: "AUD",
        unsupported: "FILE",
      };
      return map[this.kind] || "FILE";
    },
    fileIconSrc() {
      if (!this.fileItem) return DEFAULT_ICON;
      const ext = getFileExtension(this.fileItem.name || "");
      return ICON_BY_EXT[ext] || DEFAULT_ICON;
    },
    canPopOut() {
      return !!(this.fileItem && this.fileItem.id && this.groupId && shouldOpenInPreview(this.fileItem));
    },
  },
  watch: {
    value(open) {
      if (!open) this.fullscreen = false;
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    popOut() {
      if (!this.canPopOut) return;
      const url = `/file-preview?groupId=${encodeURIComponent(this.groupId)}&itemId=${encodeURIComponent(this.fileItem.id)}`;
      const width = Math.min(1400, window.screen.width - 100);
      const height = Math.min(900, window.screen.height - 100);
      const left = (window.screen.width - width) / 2;
      const top = (window.screen.height - height) / 2;
      window.open(
        url,
        "_blank",
        `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no`
      );
      this.close();
    },
  },
};
</script>

<style>
/* Vuetify nests the dialog as:
     .v-dialog__content (positioned, gets inline z-index from activeZIndex)
       > .v-dialog.fpm-dialog-root (content-class lands here)
   Vuetify's overlayable mixin sets the backdrop's inline z-index to
   (activeZIndex - 1), where activeZIndex = max(.v-dialog__content z-index,
   stackMinZIndex) + 2. Putting `!important` on the wrapper traps it at our
   value while Vuetify still bumps the overlay to value+1 inline — overlay
   ends up on top of the modal. Lift the wrapper WITHOUT !important so
   Vuetify's bump (activeZIndex = value + 2) wins on the inline style and
   the overlay naturally lands one step below it. */
.v-dialog__content:has(> .fpm-dialog-root) {
  z-index: 2147483645;
}
.fpm-dialog-root {
  box-shadow: 0 30px 80px -24px rgba(43, 43, 65, 0.35),
              0 10px 30px -8px rgba(77, 90, 111, 0.25) !important;
  border-radius: 0.8rem !important;
  overflow: hidden;
  background: #fff;
}
.fpm-dialog-root--fs {
  border-radius: 0 !important;
  box-shadow: none !important;
}
</style>

<style scoped>
/* === FilePreviewModal — Legalleria === */

.fpm {
  display: flex;
  flex-direction: column;
  background: #fff;
  height: min(88vh, 960px);
  max-height: 88vh;
  color: #2b2b41;
  font-family: "Barlow", sans-serif;
}
.fpm--fs {
  height: 100vh;
  max-height: none;
}

/* Header ------------------------------------------------ */

.fpm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.1rem 1.4rem;
  background: #fff;
  border-bottom: 1px solid rgba(43, 43, 65, 0.08);
  flex-shrink: 0;
}

.fpm-header-left {
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 0;
  flex: 1 1 auto;
}

.fpm-thumb {
  width: 2.4rem;
  height: 2.4rem;
  flex-shrink: 0;
  object-fit: contain;
  opacity: 0.85;
}

.fpm-title {
  min-width: 0;
  flex: 1 1 auto;
}

.fpm-filename {
  margin: 0 0 0.35rem 0;
  font-size: 1.3rem;
  font-weight: 600;
  line-height: 1.25;
  color: #2b2b41;
  letter-spacing: -0.005em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.fpm-meta {
  display: flex;
  gap: 0.45rem;
  align-items: center;
  font-size: 0.85rem;
  color: #6b6b7d;
  font-weight: 500;
}

.fpm-chip {
  padding: 0.15rem 0.55rem;
  background: #eceef3;
  color: #3325AE;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.fpm-dot {
  color: #b8b8c1;
}

.fpm-actions {
  display: flex;
  gap: 0.3rem;
  flex-shrink: 0;
}

.fpm-icon-btn {
  width: 2.4rem;
  height: 2.4rem;
  border: none;
  background: #eceef3;
  color: #2b2b41;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  transition: background 160ms ease, color 160ms ease, transform 160ms ease;
}
.fpm-icon-btn:hover {
  background: #fff;
  box-shadow: 0 2px 8px rgba(43, 43, 65, 0.12);
  color: #3325AE;
}
.fpm-icon-btn--close:hover {
  background: #fff;
  color: #d33;
}

/* Buttons / Links --------------------------------------- */

.fpm-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.55rem 1.2rem;
  border-radius: 25px;
  font-size: 0.95rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  border: none;
  text-decoration: none;
  transition: background 160ms ease, color 160ms ease, transform 160ms ease, box-shadow 160ms ease;
}
.fpm-btn--primary {
  background: #3325AE;
  color: #fff;
}
.fpm-btn--primary:hover {
  background: #2a1f8f;
  color: #fff;
  box-shadow: 0 6px 16px -4px rgba(51, 37, 174, 0.4);
  transform: translateY(-1px);
}
.fpm-btn--ghost {
  background: #eceef3;
  color: #2b2b41;
}
.fpm-btn--ghost:hover {
  background: #fff;
  box-shadow: 0 2px 8px rgba(43, 43, 65, 0.12);
  color: #3325AE;
}

.fpm-link {
  font-size: 0.95rem;
  font-weight: 700;
  color: #2b2b41;
  text-decoration: underline;
  text-underline-offset: 3px;
  transition: color 160ms ease;
}
.fpm-link:hover {
  color: #1e33f2;
}

/* Footer ------------------------------------------------ */

.fpm-footer {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  align-items: center;
  padding: 0.9rem 1.4rem;
  background: #fff;
  border-top: 1px solid rgba(43, 43, 65, 0.08);
  flex-shrink: 0;
}

/* Responsive -------------------------------------------- */

@media (max-width: 640px) {
  .fpm-header { padding: 0.85rem 1rem; }
  .fpm-filename { font-size: 1.1rem; }
  .fpm-meta { font-size: 0.8rem; flex-wrap: wrap; }
  .fpm-footer { padding: 0.75rem 1rem; }
  .fpm-icon-btn { width: 2.2rem; height: 2.2rem; }
}
</style>
